import { useEffect, useRef, useState } from 'react';
import { ImageUp, Pencil, Trash2 } from 'lucide-react';
import { WorkspaceBadge } from './WorkspaceBadge';
import { WorkspaceSummary, updateWorkspaceIcon } from '../../lib/workspaces';
import { uploadAttachment } from '../../lib/attachments';
import { useTranslation } from '../../lib/i18n';
import { Spinner } from '../Spinner';
import { useGenericBi } from '../../lib/use-generic-bi';
import styles from './WorkspaceBadgeEditor.module.css';

/** How long after the window regains focus we wait for a `change` before calling it a dismissal. */
const DISMISS_GRACE_MS = 400;

/**
 * The workspace thumbnail in the page header, and what it becomes while the name is being edited.
 *
 * At rest it is the plain badge. In edit mode it takes the design's edit layer — the tile keeps its
 * artwork under a 60% ink scrim with the 16px pencil centred on it (Figma 11246-166792) — and becomes
 * a button that opens the image options: upload a new photo, or remove the one that's there. This is
 * web's idiom for native's `editableBadge` → confirmation dialog (Take photo / Choose from library /
 * Remove photo): one file input covers both native pick paths, since a mobile browser's file picker
 * already offers the camera.
 *
 * The upload is the ordinary attachment path (`/api/upload-url` → direct WixMP PUT), so a phone photo
 * gets the same downscale/compress treatment as a chat image before it is stored as the icon.
 */
export function WorkspaceBadgeEditor({
  workspace,
  editing,
  page,
  onError,
  onPickingChange,
  onSaved,
}: {
  workspace: WorkspaceSummary;
  /** True while the name field is open — the badge only becomes editable alongside it, as in native. */
  editing: boolean;
  page: string;
  /** Raises an image-update failure so the hero can show it under the name (native does the same). */
  onError: (message: string | null) => void;
  /**
   * True from the moment the file dialog opens until it is dismissed or its upload settles.
   *
   * The header uses it to stop the name field committing on blur: opening the OS dialog takes focus
   * off the page, and blur is what closes the field. Backing out of the dialog must leave the edit
   * state exactly as it was.
   */
  onPickingChange: (picking: boolean) => void;
  /** A photo change actually landed — the header closes the edit state on this, and only this. */
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  /** Refs, not state: every reader is an event handler that needs the value as of right now. */
  const pickingRef = useRef(false);
  const fileArrivedRef = useRef(false);
  const dismissWatchRef = useRef<(() => void) | null>(null);

  // Leaving edit mode closes the menu with it — the trigger is about to stop existing.
  useEffect(() => {
    if (!editing) setOpen(false);
  }, [editing]);

  // Never leave the dismissal watch attached to the window past unmount.
  useEffect(() => () => dismissWatchRef.current?.(), []);

  // Dismiss on outside pointerdown / Escape, the same way the composer's AttachMenu does — except
  // Escape is taken in the CAPTURE phase. Focus is still in the name field while this menu is open
  // (see the trigger below), and that field cancels the whole edit on Escape; capturing here stops
  // the event before React's root listener reaches it, so an open menu owns Escape and closing it
  // doesn't also throw away the name the user was typing.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const toggle = () => {
    // Opening it is a fresh attempt — don't leave a stale failure on screen (mirrors native).
    onError(null);
    setOpen((v) => !v);
    logPageClickBi({ page_name: page, button_name: 'workspace_edit_image' });
  };

  /** Persist, and report whether it landed — only a real save may close the edit state. */
  const save = async (iconUrl: string): Promise<boolean> => {
    try {
      await updateWorkspaceIcon(workspace.id, iconUrl);
      onError(null);
      return true;
    } catch {
      onError(t('workspace.imageError'));
      return false;
    }
  };

  /** The dialog is closed and nothing more is coming — hand blur back to the name field. */
  const endPicking = () => {
    if (!pickingRef.current) return;
    pickingRef.current = false;
    dismissWatchRef.current?.();
    dismissWatchRef.current = null;
    onPickingChange(false);
  };

  /**
   * Open the OS file dialog, with blur-commit suspended for as long as it (and any upload from it)
   * is outstanding.
   *
   * `cancel` on the input is the clean signal that the user backed out, and it is what normally ends
   * this. The window-focus watch is a safety net: if a browser never fires `cancel`, `picking` would
   * latch on forever and the name field would stop committing on click-away. So when the window
   * regains focus we give a change event a beat to arrive, and treat its absence as a dismissal.
   */
  const beginPicking = () => {
    pickingRef.current = true;
    fileArrivedRef.current = false;
    onPickingChange(true);
    const input = fileRef.current;
    // `cancel` is attached imperatively rather than as an `onCancel` prop: React's typings for
    // `<input>` don't carry it, but the DOM event is the only precise "user backed out" signal
    // there is. `once` so a second pick doesn't stack listeners.
    const onDialogCancel = () => endPicking();
    input?.addEventListener('cancel', onDialogCancel, { once: true });
    const onWindowFocus = () => {
      window.setTimeout(() => {
        if (!fileArrivedRef.current) endPicking();
      }, DISMISS_GRACE_MS);
    };
    window.addEventListener('focus', onWindowFocus);
    dismissWatchRef.current = () => {
      window.removeEventListener('focus', onWindowFocus);
      input?.removeEventListener('cancel', onDialogCancel);
    };
    input?.click();
  };

  /**
   * The picker has closed. `file` is undefined when the user backed out of the dialog, and then
   * NOTHING happens: no error, no save, and the edit state is left exactly as they found it.
   */
  const onPicked = async (file: File | undefined) => {
    if (!file) {
      endPicking();
      return;
    }
    onError(null);
    setUploading(true);
    try {
      const result = await uploadAttachment(file, { imageOnly: true });
      if (!result.ok) {
        // A failed upload keeps the edit state open with the reason showing, so the user can retry.
        onError(t('workspace.imageError'));
        return;
      }
      if (await save(result.attachment.url)) {
        // The ONLY path that closes the edit state: a chosen file that uploaded AND persisted.
        // Ordered before `endPicking` so blur is still suspended while the header commits.
        onSaved();
      }
    } finally {
      setUploading(false);
      endPicking();
    }
  };

  if (!editing) return <WorkspaceBadge ws={workspace} size={42} />;

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={styles.trigger}
        // Opens on MOUSEDOWN with the default prevented, so the pointer never moves focus off the
        // name field. That matters more than it looks: the field commits on blur and leaves edit
        // mode, which unmounts this very trigger — so a plain onClick handler could open a menu
        // that was already gone. `onClick` is kept for keyboard activation only (Enter/Space
        // report detail 0), where there is no mousedown to have handled it.
        onMouseDown={(e) => {
          e.preventDefault();
          toggle();
        }}
        onClick={(e) => {
          if (e.detail === 0) toggle();
        }}
        disabled={uploading}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('workspace.editImage')}
        title={t('workspace.editImage')}
      >
        <WorkspaceBadge ws={workspace} size={42} />
        <span className={styles.editLayer} aria-hidden>
          {uploading ? <Spinner /> : <Pencil size={16} />}
        </span>
      </button>

      {open && (
        <div role="menu" className={styles.menu}>
          <button
            type="button"
            role="menuitem"
            className={styles.item}
            // Same reason as the trigger: taking focus off the name field would commit it and
            // unmount this menu before the click resolved.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setOpen(false);
              beginPicking();
            }}
          >
            <span className={styles.itemIcon} aria-hidden>
              <ImageUp size={16} />
            </span>
            <span>{t('workspace.uploadPhoto')}</span>
          </button>
          {/* Only offered when there is something to clear, as in native. */}
          {workspace.icon && (
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setOpen(false);
                // No OS dialog on this path, so focus never left the field — but a completed photo
                // change is a completed photo change, so it closes the edit state just like an upload.
                void save('').then((ok) => {
                  if (ok) onSaved();
                });
              }}
            >
              <span className={styles.itemIcon} aria-hidden>
                <Trash2 size={16} />
              </span>
              <span>{t('workspace.removePhoto')}</span>
            </button>
          )}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Set synchronously, so the dismissal watch knows a real pick beat it to the punch.
          fileArrivedRef.current = !!file;
          // Reset first, so picking the SAME file again still fires a change event.
          e.target.value = '';
          void onPicked(file);
        }}
      />
    </div>
  );
}
